import { useMemo } from "react";
import {
  JsonParam,
  NumberParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import { SortDirection } from "@ticketingplatform/api/dist/__generated__/globalTypes";

import { SorterParam } from "./utils";
import {
  UseTableQueryParamsOptions,
  UseTableQueryParamsOrderBy,
  UseTableQueryParamsReturnType,
  UseTableQueryParamsSortOrder,
} from "./types";

export const useTableQueryParams = <T = unknown>({
  defaultValue,
}: UseTableQueryParamsOptions<T> = {}): UseTableQueryParamsReturnType<T> => {
  const paramConfigMap = useMemo(
    () => ({
      page: withDefault(NumberParam, defaultValue?.page ?? 1),
      pageSize: withDefault(NumberParam, defaultValue?.pageSize ?? 10),
      sorter: withDefault(SorterParam<T>(), defaultValue?.sorter),
      filters: withDefault(JsonParam, defaultValue?.filters),
    }),
    [
      defaultValue?.filters,
      defaultValue?.page,
      defaultValue?.pageSize,
      defaultValue?.sorter,
    ]
  );

  const [{ page, pageSize, sorter, filters }, setQueryParams] =
    useQueryParams(paramConfigMap);

  const sorters = useMemo(() => {
    if (!sorter) {
      return [];
    }

    return Array.isArray(sorter) ? sorter : [sorter];
  }, [sorter]);

  const sortOrder = useMemo(
    () =>
      sorters.reduce<UseTableQueryParamsSortOrder<T>>(
        (reduced, { columnKey, order }) => {
          if (!columnKey || !order) {
            return reduced;
          }

          return { ...reduced, [columnKey]: order };
        },
        {}
      ),
    [sorters]
  );

  const orderBy = useMemo(
    () =>
      sorters.reduce<UseTableQueryParamsOrderBy<T>>(
        (reduced, { columnKey, order }) => {
          let sortDirection: SortDirection | undefined;

          if (order === "ascend") {
            sortDirection = SortDirection.asc;
          } else if (order === "descend") {
            sortDirection = SortDirection.desc;
          }

          if (!columnKey || !sortDirection) {
            return reduced;
          }

          return [
            ...reduced,
            { [columnKey]: sortDirection } as Partial<
              Record<keyof T, SortDirection>
            >,
          ];
        },
        []
      ),
    [sorters]
  );

  return useMemo(
    () => [
      {
        page,
        pageSize,
        sorter,
        filters,
        orderBy,
        sortOrder,
      },
      setQueryParams,
    ],
    [filters, orderBy, page, pageSize, setQueryParams, sortOrder, sorter]
  );
};
